/**
 * Catálogo de permisos granulares. Los roles son solo agrupaciones por defecto: cada asignación de rol o permiso
 * puede restringirse a empresa, centro, departamento o equipo. El backend SIEMPRE comprueba permisos.
 */
export const PERMISSIONS = {
  'company.view': 'Ver datos de la empresa',
  'company.manage': 'Editar datos y configuración de la empresa',
  'employees.view': 'Ver trabajadores',
  'employees.create': 'Crear e invitar trabajadores',
  'employees.edit': 'Editar trabajadores',
  'employees.deactivate': 'Dar de baja trabajadores',
  'employees.import': 'Importar trabajadores (CSV/XLSX)',
  'centers.manage': 'Gestionar centros de trabajo',
  'org.manage': 'Gestionar departamentos y equipos',
  'time_records.view': 'Ver registros horarios',
  'time_records.edit': 'Registrar fichajes administrativos (siempre como corrección auditada)',
  'time_records.close': 'Cerrar periodos',
  'incidents.view': 'Ver incidencias',
  'incidents.manage': 'Gestionar incidencias (crear, comentar, cerrar)',
  'incidents.approve': 'Aprobar o rechazar incidencias',
  'corrections.view': 'Ver solicitudes de corrección',
  'corrections.approve': 'Aprobar o rechazar correcciones',
  'absences.view': 'Ver vacaciones y ausencias',
  'absences.approve': 'Aprobar o rechazar vacaciones y ausencias',
  'absences.manage': 'Configurar tipos de ausencia y saldos',
  'schedules.manage': 'Gestionar horarios, turnos y asignaciones',
  'calendars.manage': 'Gestionar calendarios y festivos',
  'policies.manage': 'Gestionar políticas de jornada',
  'gps.view': 'Ver ubicaciones de fichajes',
  'gps.manage': 'Configurar GPS y radios',
  'presence.request': 'Solicitar verificaciones puntuales de presencia',
  'kiosks.manage': 'Gestionar kioscos',
  'notifications.manage': 'Configurar notificaciones',
  'reports.view': 'Ver informes',
  'reports.export': 'Exportar informes y paquetes',
  'audit.view': 'Ver auditoría',
  'users.manage': 'Gestionar usuarios, roles y permisos',
  'billing.manage': 'Gestionar suscripción y facturación',
  'integrations.manage': 'Gestionar API keys y webhooks',
  'custody.manage': 'Gestionar Cloud y Custody',
  'legal.manage': 'Gestionar documentos legales y retención',
  // Permisos propios del trabajador (siempre sobre sus propios datos)
  'self.clock': 'Fichar',
  'self.view': 'Ver mis jornadas y registros',
  'self.requests': 'Crear incidencias, correcciones y solicitudes de ausencia',
} as const;

export type Permission = keyof typeof PERMISSIONS;
export const ALL_PERMISSIONS = Object.keys(PERMISSIONS) as Permission[];

export type ScopeType = 'COMPANY' | 'CENTER' | 'DEPARTMENT' | 'TEAM';

export interface Grant {
  permission: Permission;
  scopeType: ScopeType;
  scopeId: string | null;
}

export interface EmployeeScopeTarget {
  employeeId: string;
  centerIds: string[];
  departmentId: string | null;
  teamIds: string[];
}

const ADMIN_EXCLUDED: Permission[] = [];

export const SYSTEM_ROLES: Record<'ADMIN' | 'MANAGER' | 'EMPLOYEE' | 'AUDITOR', { name: string; description: string; permissions: Permission[] }> = {
  ADMIN: {
    name: 'Administrador',
    description: 'Gestión completa de la empresa.',
    permissions: ALL_PERMISSIONS.filter((p) => !ADMIN_EXCLUDED.includes(p)),
  },
  MANAGER: {
    name: 'Responsable',
    description: 'Gestiona trabajadores, incidencias y solicitudes de su ámbito (centro, departamento o equipo).',
    permissions: [
      'employees.view',
      'time_records.view',
      'incidents.view',
      'incidents.manage',
      'incidents.approve',
      'corrections.view',
      'corrections.approve',
      'absences.view',
      'absences.approve',
      'gps.view',
      'reports.view',
      'reports.export',
      'self.clock',
      'self.view',
      'self.requests',
    ],
  },
  EMPLOYEE: {
    name: 'Trabajador',
    description: 'Ficha y consulta únicamente sus propios datos.',
    permissions: ['self.clock', 'self.view', 'self.requests'],
  },
  AUDITOR: {
    name: 'Auditor (solo lectura)',
    description: 'Consulta registros, informes, incidencias y auditoría. No puede editar ni aprobar.',
    permissions: ['company.view', 'employees.view', 'time_records.view', 'incidents.view', 'corrections.view', 'absences.view', 'reports.view', 'reports.export', 'audit.view', 'gps.view'],
  },
};

/** Permisos que nunca puede tener un rol de solo lectura (auditor), aunque se le asignen por error. */
export const WRITE_PERMISSIONS: Permission[] = ALL_PERMISSIONS.filter((p) => !p.endsWith('.view') && p !== 'reports.export' && !p.startsWith('self.'));

/** ¿Alguna concesión cubre el permiso para la empresa entera? */
export function hasCompanyWide(grants: Grant[], permission: Permission): boolean {
  return grants.some((g) => g.permission === permission && g.scopeType === 'COMPANY');
}

export function hasAny(grants: Grant[], permission: Permission): boolean {
  return grants.some((g) => g.permission === permission);
}

/** ¿Alguna concesión cubre el permiso sobre un trabajador concreto (según su centro, departamento o equipo)? */
export function canOnEmployee(grants: Grant[], permission: Permission, target: EmployeeScopeTarget): boolean {
  return grants.some((g) => {
    if (g.permission !== permission) return false;
    switch (g.scopeType) {
      case 'COMPANY':
        return true;
      case 'CENTER':
        return g.scopeId !== null && target.centerIds.includes(g.scopeId);
      case 'DEPARTMENT':
        return g.scopeId !== null && target.departmentId === g.scopeId;
      case 'TEAM':
        return g.scopeId !== null && target.teamIds.includes(g.scopeId);
    }
  });
}

/** Ámbitos (centros/departamentos/equipos) en los que se tiene un permiso; null = toda la empresa. */
export function scopesFor(grants: Grant[], permission: Permission): null | { centerIds: string[]; departmentIds: string[]; teamIds: string[] } {
  const relevant = grants.filter((g) => g.permission === permission);
  if (relevant.some((g) => g.scopeType === 'COMPANY')) return null;
  return {
    centerIds: relevant.filter((g) => g.scopeType === 'CENTER').map((g) => g.scopeId!),
    departmentIds: relevant.filter((g) => g.scopeType === 'DEPARTMENT').map((g) => g.scopeId!),
    teamIds: relevant.filter((g) => g.scopeType === 'TEAM').map((g) => g.scopeId!),
  };
}

/** Agrupación de permisos para pantallas de administración. */
export const PERMISSION_GROUPS: { label: string; permissions: Permission[] }[] = [
  { label: 'Empresa', permissions: ['company.view', 'company.manage', 'centers.manage', 'org.manage'] },
  { label: 'Trabajadores', permissions: ['employees.view', 'employees.create', 'employees.edit', 'employees.deactivate', 'employees.import'] },
  { label: 'Registro horario', permissions: ['time_records.view', 'time_records.edit', 'time_records.close', 'gps.view', 'gps.manage', 'presence.request', 'kiosks.manage'] },
  { label: 'Incidencias y solicitudes', permissions: ['incidents.view', 'incidents.manage', 'incidents.approve', 'corrections.view', 'corrections.approve', 'absences.view', 'absences.approve', 'absences.manage'] },
  { label: 'Planificación', permissions: ['schedules.manage', 'calendars.manage', 'policies.manage', 'notifications.manage'] },
  { label: 'Informes y auditoría', permissions: ['reports.view', 'reports.export', 'audit.view'] },
  { label: 'Administración', permissions: ['users.manage', 'billing.manage', 'integrations.manage', 'custody.manage', 'legal.manage'] },
  { label: 'Trabajador', permissions: ['self.clock', 'self.view', 'self.requests'] },
];
